import { BRAND } from './brand';
import { DEFAULT_PRIMARY_CTA } from './shared-ctas';
import { V4_BRAND } from './v4-brand';
import type { PageCopy } from './page-copy-types';

// ─── /double-down (301 redirect → /simple-setup) ──────────────────────────
export const DOUBLE_DOWN_COPY: PageCopy = {
  h1: 'Double Down',
  lede: '301 redirect — the Double Down campaign was renamed to Simple Setup. This route preserves backlinks from AlternativeTo, the blog post, social posts, and existing customer emails.',
  // canonical-divergence: redirect-shell CTA continues to the renamed page (not the trial signup).
  ctaPrimary: { label: 'Continue to Simple Setup', href: '/simple-setup' },
  sections: [],
  metadata: {
    title: 'Double Down → Simple Setup',
    description: 'Double Down was renamed to Simple Setup.',
    canonical: 'https://bizrnr.com/simple-setup',
  },
};

// ─── /always-answers (301 redirect → /ai-business-page) ───────────────────
export const ALWAYS_ANSWERS_COPY: PageCopy = {
  h1: 'Always Answers',
  lede: '301 redirect — /always-answers was renamed to /ai-business-page. The rename reflects the brand system where the rotating hero outcome is "Always Answering" and the pillar that delivers it is the "AI Business Page".',
  // canonical-divergence: redirect shell.
  ctaPrimary: { label: 'Continue to AI Business Page', href: '/ai-business-page' },
  sections: [],
  metadata: {
    title: 'Always Answers → AI Business Page',
    description: 'Always Answers was renamed to AI Business Page.',
    canonical: 'https://bizrnr.com/ai-business-page',
  },
};

// ─── /always-open (301 redirect → /simple-setup) ──────────────────────────
export const ALWAYS_OPEN_COPY: PageCopy = {
  h1: 'Always Open',
  lede: '301 redirect — /always-open was renamed to /simple-setup. The rename reflects the brand system where the rotating hero outcome is "Always On" and the pillar that delivers it is called "Simple Setup".',
  // canonical-divergence: redirect shell.
  ctaPrimary: { label: 'Continue to Simple Setup', href: '/simple-setup' },
  sections: [],
  metadata: {
    title: 'Always Open → Simple Setup',
    description: 'Always Open was renamed to Simple Setup.',
    canonical: 'https://bizrnr.com/simple-setup',
  },
};

// ─── /near-me ─────────────────────────────────────────────────────────────
export const NEAR_ME_COPY: PageCopy = {
  eyebrow: 'cities · industries',
  h1: 'AI receptionist near me — find your city',
  lede:
    'Looking for an AI voice agent that works in your market? BizRnR serves 25+ US cities across HVAC, dental, real estate, legal, and home services. Pick your city + industry below to see local use cases, setup, and pricing. Same $99/mo AI Receptionist plan everywhere.',
  ctaPrimary: DEFAULT_PRIMARY_CTA,
  sections: [
    { heading: 'Pick your city' },
    { heading: 'Or pick your industry' },
    { heading: 'Common questions about local AI answering' },
    { heading: 'Get started in any city', body: `Under ${BRAND.setupTimeClaim}. 30-day guarantee. Same plan everywhere.` },
  ],
  faqs: [
    { q: 'Does BizRnR work in my city?', a: 'BizRnR serves all of the United States and Canada. Dedicated local landing pages exist for major metros; other markets get the same service through our main platform.' },
    { q: 'Does pricing vary by city?', a: 'No. BizRnR AI Receptionist is $99/month everywhere with 100 AI interactions included. No geographic surcharges.' },
    { q: 'Can BizRnR handle local area codes and phone numbers?', a: 'Yes. We provision a local number in any US area code at signup, or you can port an existing number from your carrier.' },
    { q: 'Does it know my local area — neighborhoods, landmarks, businesses?', a: 'For booking and qualification, yes — you customize the AI with your service area and local knowledge during setup. For small-talk about local sports teams or weather, no.' },
  ],
  metadata: {
    title: 'AI Receptionist Near Me — Cities & Industries Served',
    description:
      'Find an AI voice agent near you. BizRnR serves 25+ US cities across HVAC, dental, real estate, legal, and home services. Pick your city + industry to see local setup, use cases, and pricing.',
    canonical: 'https://bizrnr.com/near-me',
  },
};

// ─── /request-demo ────────────────────────────────────────────────────────
// form lives in src/components/demo/DemoLeadForm.tsx
export const REQUEST_DEMO_COPY: PageCopy = {
  eyebrow: 'Live Demo',
  h1: 'See BizRnR handle your first call',
  lede:
    'A 5-minute walkthrough of the AI voice agent, lead intelligence dashboard, and live call handoff — tailored to your industry. 30-day guarantee, no commitment.',
  // canonical-divergence: demo page CTA routes to homepage Bri voice flow — voice-first by design.
  ctaPrimary: { label: `${V4_BRAND.fabLabel} on the homepage →`, href: '/' },
  sections: [
    {
      heading: 'What you’ll see',
      bullets: [
        'Live AI voice call — the same agent that answers for your business.',
        'Lead enrichment pipeline — owner names, direct mobile, verified email.',
        'Outreach in action — SMS, email, and voice from one dashboard.',
        'Pricing + setup time for your specific industry.',
      ],
    },
    { heading: 'Want to try it immediately?' },
  ],
  metadata: {
    title: 'Request a Demo — BizRnR AI Voice Automation',
    description:
      'See BizRnR in action. Get a 5-minute walkthrough of the AI voice agent, lead intelligence, and live call handoff — tailored to your industry.',
    canonical: 'https://bizrnr.com/request-demo',
  },
};

// ─── /demo (search-param redirect) ────────────────────────────────────────
// dynamic redirect to /demo/p/[slug] based on ?name=&business= query
export const DEMO_COPY: PageCopy = {
  h1: 'Demo redirect',
  lede:
    '/demo?name=...&business=... handler. This route handles the fallback URL format sent via SMS/email when the slug-based demo page URL wasn’t available at send time.',
  // canonical-divergence: redirect shell — falls back to homepage.
  ctaPrimary: { label: 'Go to homepage', href: '/' },
  sections: [],
  metadata: {
    title: 'BizRnR Demo',
    description: 'Personalized BizRnR demo redirect.',
    canonical: 'https://bizrnr.com/demo',
  },
};
